import * as React from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { BarChart3 } from 'lucide-react'
import {
  AreaChart,
  Area,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts'
import { ChartDataPoint } from '@/utils/generate-chart-data'
import { useTheme } from '@/contexts/theme.context'

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    name: string
    value: number | string
    color: string
    dataKey?: string
    payload: ChartDataPoint
  }>
}

export function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (active && payload && payload.length) {
    // Get the data point from the first payload entry
    // All entries in payload should have the same payload (data point)
    const data = payload[0]?.payload as ChartDataPoint | undefined

    // Ensure we have valid data
    if (!data || !data.fullDate) {
      return null
    }

    // Always use fullDate from the payload data point, not the label
    // This ensures we show the correct date for the hovered point
    const displayDate = data.fullDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })

    return (
      <div className="bg-white dark:bg-background border border-gray-200 dark:border-border rounded-lg shadow-xl p-4 backdrop-blur-sm">
        <p className="text-sm font-semibold text-gray-900 dark:text-foreground mb-3 border-b border-gray-200 dark:border-border pb-2">
          {displayDate}
        </p>
        <div className="space-y-2">
          {payload.map((entry, index) => {
            // Get the actual value from the data point based on the dataKey
            // Recharts passes dataKey in the entry, use it to get the correct value
            let actualValue: number | undefined

            if (entry.dataKey && data) {
              // Access the value directly from the data point using the dataKey
              actualValue = data[entry.dataKey as keyof ChartDataPoint] as
                | number
                | undefined
            }

            // Use entry.value as fallback, but ensure it's a number
            // Recharts sometimes passes the value correctly in entry.value
            const numericValue =
              typeof entry.value === 'number' ? entry.value : undefined
            const displayValue =
              actualValue !== undefined ? actualValue : (numericValue ?? 0)

            return (
              <div
                key={`${entry.name}-${index}`}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm text-gray-600 dark:text-muted-foreground">
                    {entry.name}
                  </span>
                </div>
                <span
                  className="text-sm font-bold"
                  style={{ color: entry.color }}
                >
                  {displayValue}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
  return null
}

export function EmptyChartState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-[380px] text-center px-4">
      <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-muted flex items-center justify-center mb-4">
        <BarChart3 className="h-8 w-8 text-gray-400 dark:text-muted-foreground" />
      </div>
      <p className="text-gray-500 dark:text-muted-foreground font-medium mb-1">
        No data available
      </p>
      <p className="text-sm text-gray-400 dark:text-muted-foreground">
        {message}
      </p>
    </div>
  )
}

export function ChartLoadingState() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const skeletonColor = isDark ? '#374151' : '#d1d5db'
  const [tick, setTick] = React.useState(0)

  // Update continuously every 800ms to keep points changing
  React.useEffect(() => {
    const interval = setInterval(() => {
      setTick(prev => prev + 1)
    }, 2000)

    return () => clearInterval(interval)
  }, [])

  // Generate 30 points for a month of data with some variation
  // Regenerates every tick to create flowing effect
  const skeletonData = React.useMemo(
    () =>
      Array.from({ length: 30 }, (_, i) => {
        const date = new Date()
        date.setDate(date.getDate() - (29 - i))
        const base = 30 + Math.random() * 25
        const trend = i * 0.6
        const noise = (Math.random() - 0.5) * 12

        return {
          date: date.toISOString().split('T')[0],
          timestamp: date.getTime(),
          value: Math.round(base + trend + noise),
          dateLabel: '',
          fullDate: date,
        }
      }),
    [tick],
  )

  return (
    <div className="space-y-4 animate-pulse">
      <div className="flex items-center justify-end gap-3 sm:gap-6 pt-4 pr-6 pb-2">
        <div className="h-3 w-32 bg-gray-200 dark:bg-muted rounded" />
        <div className="h-3 w-32 bg-gray-200 dark:bg-muted rounded" />
      </div>
      <ResponsiveContainer
        width="100%"
        height={280}
        className="min-h-[280px] sm:min-h-[320px]"
      >
        <LineChart
          data={skeletonData}
          margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={isDark ? '#2a2a2a' : '#f0f0f0'}
            vertical={false}
          />
          <XAxis
            dataKey="timestamp"
            stroke={skeletonColor}
            style={{ fontSize: '10px', fontWeight: 500 }}
            tickLine={false}
            axisLine={false}
            tick={{ fill: skeletonColor }}
            type="number"
            scale="linear"
            domain={['dataMin', 'dataMax']}
            tickFormatter={() => ''}
          />
          <YAxis
            stroke={skeletonColor}
            style={{ fontSize: '11px', fontWeight: 500 }}
            tickLine={false}
            axisLine={false}
            tick={{ fill: skeletonColor }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={skeletonColor}
            strokeWidth={3}
            dot={{ fill: skeletonColor, r: 2 }}
            isAnimationActive={true}
            animationDuration={4000}
            animationEasing="ease-in-out"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

interface SingleChartProps {
  title: string
  description: string
  dataKey1: string
  dataKey2?: string
  color1: string
  color2?: string
  name1: string
  name2?: string
  icon: React.ComponentType<{ className?: string }>
  hasData: boolean
  chartType: 'line' | 'bar' | 'area'
  chartData: ChartDataPoint[]
  isLoading?: boolean
}

export function SingleChart({
  dataKey1,
  dataKey2,
  color1,
  color2,
  name1,
  name2,
  hasData,
  chartType,
  chartData,
  isLoading = false,
}: SingleChartProps) {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const gridColor = isDark ? '#2a2a2a' : '#f0f0f0'
  const axisTickColor = isDark ? '#9ca3af' : '#6b7280'
  const cursorColor = isDark ? '#374151' : '#e5e7eb'
  const hasSecondSeries = Boolean(dataKey2 && color2 && name2)
  // Create a formatter function that has access to chartData
  const formatTick = (value: number | string, index?: number) => {
    // For numeric timestamps, find the matching data point
    if (typeof value === 'number') {
      const point = chartData?.find((d: any) => d.timestamp === value)
      if (point?.dateLabel) {
        return point.dateLabel
      }
      // Fallback: format from timestamp
      if (value) {
        const date = new Date(value)
        const month = date.toLocaleDateString('en-US', { month: 'short' })
        const day = date.getDate()
        return `${month} ${day}`
      }
    }
    // For string values (category type fallback)
    if (
      typeof value === 'string' &&
      chartData &&
      index !== undefined &&
      chartData[index]
    ) {
      const dataPoint = chartData[index] as any
      if (dataPoint.dateLabel) {
        return dataPoint.dateLabel
      }
      // Fallback: format ISO date string
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const date = new Date(value)
        const month = date.toLocaleDateString('en-US', { month: 'short' })
        const day = date.getDate()
        return `${month} ${day}`
      }
    }
    return value?.toString() || ''
  }

  return (
    <Card className="bg-white dark:bg-background shadow-sm border border-gray-200 dark:border-border hover:shadow-md transition-shadow">
      <CardContent className="p-0">
        {isLoading ? (
          <ChartLoadingState />
        ) : !hasData ? (
          <EmptyChartState
            message={`No data available for the selected period`}
          />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-end gap-3 sm:gap-6 pt-4 pr-6 pb-2">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color1 }}
                />
                <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-muted-foreground">
                  {name1}
                </span>
              </div>
              {hasSecondSeries && (
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color2 }}
                  />
                  <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-muted-foreground">
                    {name2}
                  </span>
                </div>
              )}
            </div>
            <ResponsiveContainer
              key={`chart-${chartType}-${dataKey1}-${dataKey2 || 'single'}`}
              width="100%"
              height={280}
              className="min-h-[280px] sm:min-h-[320px]"
            >
              {chartType === 'line' ? (
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                  key={`linechart-${chartData?.length || 0}-${chartData?.[0]?.date || ''}-${chartData?.[chartData?.length - 1]?.date || ''}`}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={gridColor}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="timestamp"
                    stroke={axisTickColor}
                    style={{ fontSize: '10px', fontWeight: 500 }}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: axisTickColor }}
                    type="number"
                    scale="linear"
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={formatTick}
                  />
                  <YAxis
                    stroke={axisTickColor}
                    style={{ fontSize: '11px', fontWeight: 500 }}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: axisTickColor }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ stroke: cursorColor, strokeWidth: 1 }}
                  />
                  <Line
                    key={`line-${dataKey1}`}
                    type="monotone"
                    dataKey={dataKey1}
                    stroke={color1}
                    strokeWidth={3}
                    dot={{ fill: color1, r: 2 }}
                    activeDot={{ r: 4 }}
                    name={name1}
                    connectNulls={false}
                    isAnimationActive={false}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {hasSecondSeries && dataKey2 && color2 && name2 && (
                    <Line
                      key={`line-${dataKey2}`}
                      type="monotone"
                      dataKey={dataKey2}
                      stroke={color2}
                      strokeWidth={3}
                      dot={{ fill: color2, r: 2 }}
                      activeDot={{ r: 4 }}
                      name={name2}
                      connectNulls={false}
                      isAnimationActive={false}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                </LineChart>
              ) : chartType === 'bar' ? (
                <ComposedChart
                  data={chartData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                  key={`barchart-${chartData?.length || 0}-${chartData?.[0]?.date || ''}-${chartData?.[chartData?.length - 1]?.date || ''}`}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={gridColor}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="timestamp"
                    stroke={axisTickColor}
                    style={{ fontSize: '10px', fontWeight: 500 }}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: axisTickColor }}
                    type="number"
                    scale="linear"
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={formatTick}
                  />
                  <YAxis
                    stroke={axisTickColor}
                    style={{ fontSize: '11px', fontWeight: 500 }}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: axisTickColor }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: cursorColor, strokeWidth: 0 }}
                  />
                  <Bar
                    key={`bar-${dataKey1}`}
                    dataKey={dataKey1}
                    fill={color1}
                    name={name1}
                    radius={[6, 6, 0, 0]}
                    isAnimationActive={false}
                  />
                  {hasSecondSeries && dataKey2 && color2 && name2 && (
                    <Bar
                      key={`bar-${dataKey2}`}
                      dataKey={dataKey2}
                      fill={color2}
                      name={name2}
                      radius={[6, 6, 0, 0]}
                      isAnimationActive={false}
                    />
                  )}
                </ComposedChart>
              ) : (
                <AreaChart
                  data={chartData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                  key={`areachart-${chartData?.length || 0}-${chartData?.[0]?.date || ''}-${chartData?.[chartData?.length - 1]?.date || ''}`}
                >
                  <defs>
                    <linearGradient
                      id={`gradient-${dataKey1}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor={color1} stopOpacity={0.4} />
                      <stop
                        offset="95%"
                        stopColor={color1}
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                    {hasSecondSeries && color2 && (
                      <linearGradient
                        id={`gradient-${dataKey2}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={color2}
                          stopOpacity={0.4}
                        />
                        <stop
                          offset="95%"
                          stopColor={color2}
                          stopOpacity={0.05}
                        />
                      </linearGradient>
                    )}
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={gridColor}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="timestamp"
                    stroke={axisTickColor}
                    style={{ fontSize: '10px', fontWeight: 500 }}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: axisTickColor }}
                    type="number"
                    scale="linear"
                    domain={['dataMin', 'dataMax']}
                    tickFormatter={formatTick}
                  />
                  <YAxis
                    stroke={axisTickColor}
                    style={{ fontSize: '11px', fontWeight: 500 }}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: axisTickColor }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ stroke: cursorColor, strokeWidth: 1 }}
                  />
                  <Area
                    key={`area-${dataKey1}`}
                    type="monotone"
                    dataKey={dataKey1}
                    stroke={color1}
                    fill={`url(#gradient-${dataKey1})`}
                    name={name1}
                    strokeWidth={2.5}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                    isAnimationActive={false}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    stackId={undefined}
                  />
                  {hasSecondSeries && dataKey2 && color2 && name2 && (
                    <Area
                      key={`area-${dataKey2}`}
                      type="monotone"
                      dataKey={dataKey2}
                      stroke={color2}
                      fill={`url(#gradient-${dataKey2})`}
                      name={name2}
                      strokeWidth={2.5}
                      activeDot={{ r: 5 }}
                      connectNulls={false}
                      isAnimationActive={false}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      stackId={undefined}
                    />
                  )}
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
